import cx from 'classnames';
import React, { useCallback, useMemo, useRef } from 'react';
import { Button } from 'ui';
import bt from '../bootstrap.module.css';
import FormElementWrapper from '../components/FormElementWrapper';
import ComponentLabel from '../components/component-label';
import useGetFormGeneratorItemError from '../hooks/useGetFormGeneratorItemError';
import icons from '../icons';

const FileUpload = props => {
  const { value = [], onChange, data, disabled, editing, isBuilder, itemValue = [] } = props;
  const { requiredError } = useGetFormGeneratorItemError(data.id);
  const inputRef = useRef();
  const files = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const handleChange = useCallback(
    e => {
      if (!e?.target?.files) return;

      const newFiles = Array.from(e.target.files);
      onChange(data.type, data.id, [...files, ...newFiles]);

      // reset input so same file can be re-uploaded
      e.target.value = '';
    },
    [files, data.id, data.type, onChange],
  );

  const handleRemove = useCallback(
    index => {
      const next = files.filter((_, i) => i !== index);
      onChange(data.type, data.id, next);
    },
    [files, data.id, data.type, onChange],
  );

  const handleClear = useCallback(() => {
    onChange(data.type, data.id, []);
  }, [data.id, data.type, onChange]);

  const acceptFormats = data.fileFormats ? data.fileFormats.map(f => `.${f}`).join(', ') : '*';

  return (
    <FormElementWrapper {...props}>
      <div className={cx('flex flex-col gap-2', bt['form-group'])}>
        <div className="flex gap-4 justify-between items-center">
          <ComponentLabel {...props} />

          {files.length > 0 && !disabled && (
            <div className="w-fit print:hidden">
              <Button
                variant="plain"
                onClick={handleClear}
                className="py-1 min-h-[30px]"
                focusable={false}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
        <p className="text-neutral-500">{data.description}</p>

        {/* UPLOAD */}
        {!disabled && (
          <div
            className="image-upload-container cursor-pointer h-[72px] border border-dashed rounded-lg p-3.5 print:hidden"
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              disabled={disabled}
              accept={acceptFormats}
              className="h-0"
              onChange={handleChange}
              onClick={e => e.stopPropagation()}
            />
            <div className="image-upload-control flex flex-col w-full justify-center cursor-pointer">
              <div className={cx(bt.btn, bt['btn-default'], 'cursor-pointer')}>
                Drop files here to upload
              </div>
              <p className="underline text-center cursor-pointer">Browse your device</p>
            </div>
          </div>
        )}

        {/* FILES LIST */}
        {files.length > 0 && (
          <div className="flex flex-col gap-2">
            {files.map((file, index) => {
              const isLocalFile = file instanceof File;
              const meta = itemValue?.[index];

              const name = isLocalFile ? file.name : meta?.filename || 'Uploaded file';

              const size = isLocalFile ? file.size : meta?.filesize;

              const item = (
                <div
                  key={index}
                  className={cx('p-[5px] border-[1px] border-gray-200 rounded', {
                    'hover:border-blue-300': !isLocalFile,
                  })}
                >
                  <div className="flex gap-3 items-center">
                    <div className="flex items-center justify-center rounded-full bg-neutral-100 w-12 h-12">
                      <img src={icons.FileDownload} className="w-6 h-6" alt="file" />
                    </div>

                    <div className="flex flex-col gap-1 flex-1 items-start">
                      <span>{name.replace(/\.[^/.]+$/, '')}</span>

                      <div className="flex items-center text-neutral-500 gap-1">
                        <span className="uppercase">{name.split('.').pop()}</span>
                        {size && (
                          <span className="pl-1 border-l">
                            {size > 1024 * 1024
                              ? `${Math.ceil(size / (1024 * 1024))} MB`
                              : `${Math.ceil(size / 1024)} KB`}
                          </span>
                        )}
                      </div>
                    </div>

                    {!disabled && (
                      <div className="w-fit print:hidden">
                        <Button variant="plain" onClick={() => handleRemove(index)}>
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );

              if (!isLocalFile) {
                return (
                  <div className="w-full" key={index}>
                    {/* screen */}
                    <a
                      className={cx(bt.btn, bt['btn-default'], 'print:hidden')}
                      href={file}
                      style={{ margin: '0 3px', padding: 0 }}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item}
                    </a>
                    {/* print */}
                    <span className="hidden print:inline">{item}</span>
                  </div>
                );
              }

              return item;
            })}
          </div>
        )}

        {files.length === 0 && !isBuilder && (disabled || !editing) && (
          <div className="rounded border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
            No file was uploaded
          </div>
        )}

        {requiredError && files.length === 0 && (
          <div style={{ color: 'red', marginTop: 4 }}>File is required</div>
        )}
      </div>
    </FormElementWrapper>
  );
};

export default FileUpload;
